import { useCallback, useEffect, useRef, useState, type TouchEvent } from "react";
import { useNavigate } from "react-router-dom";
import { heroBanners } from "@/mocks/videos";

export default function HeroCarousel() {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const count = heroBanners.length;

  const goTo = useCallback((index: number) => {
    setActive(((index % count) + count) % count);
  }, [count]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % count);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [count]);

  const handleTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) {
      goTo(delta < 0 ? active + 1 : active - 1);
    }
    touchStartX.current = null;
  };

  const banner = heroBanners[active];

  return (
    <section
      className="relative w-full h-[440px] md:h-[560px] lg:h-[620px] overflow-hidden bg-foreground-950"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {heroBanners.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.image}
            alt={`${item.title} 精选推荐`}
            title={`${item.title} 在线观看`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full max-w-[1280px] items-center px-4 md:px-6">
        <div className="w-full max-w-xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-label font-medium text-background-50">
            <i className="ri-fire-fill" />
            本周编辑精选
          </span>
          <h2 className="mt-4 font-heading text-3xl md:text-5xl font-bold leading-tight text-background-50">
            {banner.title}
          </h2>
          <p className="mt-3 max-w-lg text-sm md:text-base leading-relaxed text-background-200">
            {banner.tagline}
          </p>
          <p className="mt-2 text-xs md:text-sm text-accent-300">{banner.meta}</p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/detail/${banner.videoId}`)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-fill text-base" />
              立即观看
            </button>
            <button
              type="button"
              onClick={() => navigate(`/detail/${banner.videoId}`)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/60 bg-background-50/10 px-5 py-2.5 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
            >
              <i className="ri-information-line text-base" />
              查看详情
            </button>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => goTo(active - 1)}
        className="absolute left-3 md:left-6 top-1/2 z-20 flex h-9 w-9 md:h-11 md:w-11 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 backdrop-blur transition-colors hover:bg-primary-500"
      >
        <i className="ri-arrow-left-s-line text-xl md:text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => goTo(active + 1)}
        className="absolute right-3 md:right-6 top-1/2 z-20 flex h-9 w-9 md:h-11 md:w-11 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 backdrop-blur transition-colors hover:bg-primary-500"
      >
        <i className="ri-arrow-right-s-line text-xl md:text-2xl" />
      </button>

      <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {heroBanners.map((item, index) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${index + 1} 张`}
            onClick={() => goTo(index)}
            className={`h-1.5 rounded-full transition-all ${
              index === active ? "w-8 bg-primary-500" : "w-3 bg-background-50/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}